import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/birdwatching-cambodia-tour.jpg";

const itineraryFullDay = [
  { time: "5:30 AM", title: "Hotel Pickup", body: "Early morning pickup from your hotel in Siem Reap and transfer to the boat dock." },
  { time: "7:30 AM", title: "Packed Breakfast on Boat", body: "Enjoy a packed breakfast as you cruise across the Tonlé Sap toward Prek Toal." },
  { time: "8:30 AM", title: "Arrive Prek Toal Floating Village", body: "Arrive at the floating village on the northwestern edge of the lake." },
  { time: "8:45 AM", title: "Transfer to Community Boats", body: "Switch to smaller community boats to navigate the flooded forest channels." },
  { time: "9:30 AM", title: "Observation Platform & Treetop Views", body: "Climb the treetop observation platform for sweeping views of breeding bird colonies." },
  { time: "10:30 AM", title: "Lunch at Saray Community Restaurant", body: "Return to the village for a traditional Khmer lunch supporting the local community." },
  { time: "12:45 PM", title: "Water Hyacinth Handicraft Workshop", body: "Visit a local workshop where invasive water hyacinth is woven into beautiful handicrafts." },
  { time: "1:00 PM", title: "Paddle Boat Tour", body: "Explore the floating village by quiet paddle boat and meet local families." },
  { time: "3:15 PM", title: "Return to Siem Reap", body: "Begin the journey back across the lake and overland to Siem Reap." },
  { time: "4:00 PM", title: "Hotel Drop-off", body: "Arrive back at your hotel." },
];

const itineraryAfternoon = [
  { time: "9:30 AM", title: "Hotel Pickup", body: "Late morning pickup from your hotel in Siem Reap." },
  { time: "12:30 PM", title: "Lunch at Prek Toal", body: "Lunch at a local restaurant in the floating village." },
  { time: "2:50 PM", title: "Birding at Sanctuary Platform", body: "Arrive at the Prek Toal Waterbird Sanctuary platform and begin birding." },
  { time: "5:00 PM", title: "Return to Vehicle", body: "Cruise back to the dock and transfer to your vehicle." },
  { time: "5:30 PM", title: "Hotel Drop-off", body: "Arrive back at your hotel." },
];

const birds = [
  "Greater Adjutant","Lesser Adjutant","Painted Stork","Milky Stork","Great Cormorant","Purple Heron","Black Bittern","Cinnamon Bittern","Spot-billed Pelican","Yellow Bittern","Asian Openbill","Medium Egret","Oriental Darter","Indian Cormorant","Gray-headed Fish Eagle","Oriental Reed Warbler","Dusky Warbler","Black-browed Reed Warbler","Plain-backed Sparrow","Amur Stonechat","Pied Bush Chat","Black-collared Starling","Brown Shrike","Oriental Pratincole","Rufous Woodpecker","White-breasted Waterhen","Black-headed Ibis",
];

const highlights = ["Greater Adjutant","Milky Stork","Floating village","Ramsar wetlands","Community restaurant","150+ bird species"];

const includes = [
  "All transport including transfers",
  "All reserve entry fees",
  "Breakfast and lunch on day of departure",
  "Bottled drinking water",
  "Boat trips",
  "English-speaking birding guide",
  "Community restaurant meal",
  "Handicraft workshop visit",
];

const excludes = [
  "International flights",
  "Travel insurance",
  "Entry visa",
  "Alcoholic and soft drinks",
  "Tips",
  "Laundry",
  "Hotel mini bar",
  "Phone calls",
  "Personal expenses",
];

const TourPrekToal = () => {
  return (
    <main className="bg-background">
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Prek Toal Bird Sanctuary day tour from Siem Reap — best birdwatching spots in Cambodia with French-speaking guide"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Full Day · 11 Hours</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Prek Toal <em className="font-light">Floating Village</em> & Bird Sanctuary.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              A spectacular Ramsar site on the Tonlé Sap and part of Cambodia's UNESCO Biosphere Reserve — home to the largest breeding colony of large waterbirds in Southeast Asia, with over 150 species recorded.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · Full Day (11h)</span>
              <span>Group · 2 People</span>
              <span>Difficulty · Easy</span>
              <span>Rating · 4.9 / 5</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Sanctuary</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Where Tonlé Sap's flooded forest comes alive.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Nestled on the northwestern edge of the Tonle Sap Great Lake, the Prek Toal Bird Sanctuary is a spectacular Ramsar site and part of Cambodia's UNESCO Biosphere Reserve. This unique ecosystem, dominated by seasonally flooded freshwater swamp forest, supports thousands of rare and endangered birds during the dry season, making it one of Southeast Asia's premier birdwatching destinations.
            </p>
            <p>
              Prek Toal is home to the largest breeding colony of large waterbirds in the region, with more than 150 bird species recorded within this protected reserve.
            </p>
            <p>
              <strong className="font-medium text-foreground">Best Season:</strong> November to April, with the peak from January to March, when water levels are low and all waterbirds come to breed at the Prek Toal Waterbird Sanctuary.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary · Option 1</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
                Full Day-sunrise to late afternoon.
              </h2>
              <ol className="space-y-px mb-20">
                {itineraryFullDay.map((s, idx) => (
                  <li key={idx} className="group grid grid-cols-[auto_1fr] md:grid-cols-[180px_1fr] gap-x-6 md:gap-x-12 border-t border-border py-8 transition-colors hover:bg-secondary/40 last:border-b">
                    <div className="flex flex-col items-start gap-y-2">
                      <span className="font-display text-primary tabular-nums text-xl">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="uppercase tracking-[0.3em] text-primary text-base whitespace-nowrap">{s.time}</span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">{s.title}</h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary · Option 2</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
                Afternoon relaxed half-day birding.
              </h2>
              <ol className="space-y-px">
                {itineraryAfternoon.map((s, idx) => (
                  <li key={idx} className="group grid grid-cols-[auto_1fr] md:grid-cols-[180px_1fr] gap-x-6 md:gap-x-12 border-t border-border py-8 transition-colors hover:bg-secondary/40 last:border-b">
                    <div className="flex flex-col items-start gap-y-2">
                      <span className="font-display text-primary tabular-nums text-xl">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="uppercase tracking-[0.3em] text-primary text-base whitespace-nowrap">{s.time}</span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">{s.title}</h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar pricePerPerson={240} tourTitle="Prek Toal Floating Village & Bird Watching Sanctuary Tour" pricingTiers={[{ maxPax: 1, price: 240 }, { maxPax: 2, price: 285 }, { maxPax: 3, price: 325 }, { maxPax: 4, price: 356 }, { maxPax: 5, price: 356 }, { maxPax: 6, price: 389 }, { maxPax: 7, price: 425 }, { maxPax: 8, price: 467 }, { maxPax: 9, price: 496 }, { maxPax: 10, price: 589 }]} />
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Tour Highlights</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Six unforgettable moments.
          </h2>
          <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {highlights.map((h) => (
              <li key={h} className="border border-border bg-background px-5 py-4 font-light text-foreground">{h}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Birds You May Encounter</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            27+ species at a single sanctuary.
          </h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 font-light text-muted-foreground">
            {birds.map((b) => (
              <li key={b} className="border-b border-border pb-2">• {b}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Tour Details</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4 text-balance">
            $240 <span className="font-light">per person.</span>
          </h2>
          <p className="text-muted-foreground font-light mb-12">All-inclusive day from Siem Reap.</p>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Physical Effort</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                No long uphill hikes, but participants should be fit enough to walk up to 1 km in hot conditions, mostly on flat terrain. Within the capabilities of most healthy travellers comfortable in tropical climates.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Hot and dry weather throughout, with the occasional shower. Midday is hot but most woodland sites have low humidity. Wetland sites can be humid by nature; the tour is paced to avoid birding in peak heat.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Khmer cuisine — similar to Southeast Asian cooking but less spicy. Lodges serve a wide variety of good local food, with bread widely available and a strong selection of local brews.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">What to Bring</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                <li>• Hat & sunglasses</li>
                <li>• Sunscreen</li>
                <li>• Insect repellent</li>
                <li>• Binoculars</li>
                <li>• Comfortable walking shoes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourPrekToal;
